import URI from 'jsuri';
import moment from 'moment';

import API from 'js/lib/api';

import type { DecoratedEnterpriseContract, EnterpriseContract } from 'bundles/enterprise-admin-account/constants/types';
import {
  org_coursera_product_enterprise_contract_billing_EnterpriseContractBillingSystemType as EnterpriseContractBillingSystemType,
  org_coursera_product_enterprise_contract_EnterpriseContractState as EnterpriseContractState,
  org_coursera_product_enterprise_contract_EnterpriseContractType as EnterpriseContractType,
} from 'bundles/enterprise-admin-account/utils/__generated__/globalTypes';
import { enterpriseContractBillingType } from 'bundles/payments/lib/enterpriseContractCartUtils';

import _t from 'i18n!nls/admin-dashboard';

export const API_TAGS = {
  DETERMINE_COST: 'DETERMINE_COST',
  UPDATE_CONTRACT_LIMITS: 'UPDATE_CONTRACT_LIMITS',
};

export function getRemainingContractTime(expirationDate: number, from?: number): { months: number; days: number } {
  const fromDate = from ? moment(from) : moment();
  const duration = moment.duration(moment(expirationDate).diff(fromDate));
  const months = duration.months();
  const days = duration.days();
  return { months, days };
}

export const getContractCurrentTerm = (contract: DecoratedEnterpriseContract) => {
  const now = new Date(Date.now()).valueOf();
  return contract.terms.find((t) => t.startsAt === now || (t.startsAt < now && (!t.endsAt || now < t.endsAt)));
};

export const getRemainingDaysOnCurrentTerm = (contract: DecoratedEnterpriseContract) => {
  const currentTerm = getContractCurrentTerm(contract);
  if (!currentTerm?.endsAt) {
    return Infinity;
  }

  const remainingTime = currentTerm?.endsAt - Date.now();
  const remainingDays = remainingTime / (24 * 60 * 60 * 1000);

  return Math.ceil(remainingDays);
};

// contract termination occurs one month after the contract expires
function isContractTerminated(contract: DecoratedEnterpriseContract): boolean {
  return !!contract.endedAt;
}

export function isContractExpired(contract: DecoratedEnterpriseContract): boolean {
  if (isContractTerminated(contract)) {
    return true;
  }

  const endDate = contract.endsAt || contract.autoRenewInformation?.renewsAt;

  // If neither `endsAt` or `renewsAt` exists, we assume that the contract is not expired
  if (!endDate) {
    return false;
  }

  return !moment().isBefore(moment(endDate));
}

export function isInvoiceContract(contract: DecoratedEnterpriseContract): boolean {
  return contract.billingSystemType === EnterpriseContractBillingSystemType.AUTOMATED_INVOICE;
}

export function isInvoiceUpsellPeriod(contract: DecoratedEnterpriseContract): boolean {
  const today = new Date(Date.now()).valueOf();
  const endDate = contract.autoRenewInformation?.renewsAt || contract.endsAt;

  return !!endDate && today < endDate - 86400 * 30 * 1000;
}

export function isInvoiceContractSandbox(contract: DecoratedEnterpriseContract): boolean {
  const lastIndex = contract.previousPayments.length - 1;
  return contract.previousPayments[lastIndex].paymentInformation.paymentProcessorId === 'stripeSandbox';
}

export function isFreeTrialContract(contract: {
  contractTags?: { elements?: { contractTag?: string }[] };
  contractTag?: { contractTagName?: string };
}): boolean {
  const tagName = contract?.contractTag?.contractTagName;
  if (tagName) {
    return tagName === 'C4T_FREE_TRIAL';
  }
  return (
    contract.contractTags?.elements?.some((t) => t.contractTag === 'C4T_FREE_TRIAL') ??
    contract.contractTag?.contractTagName === 'C4T_FREE_TRIAL'
  );
}

export function getContractTerminationTime(contract: DecoratedEnterpriseContract | EnterpriseContract): number {
  const { endsAt, autoRenewInformation, endedAt } = contract as DecoratedEnterpriseContract;
  const { renewsAt } = autoRenewInformation || {};
  // Not an ideal cast, a contract should either have a renew or end date
  const endDate = (renewsAt || endsAt) as number;
  if (endedAt) {
    return moment(endDate).isBefore(moment(endedAt)) ? endDate : endedAt;
  } else {
    return endDate;
  }
}

export function getExpirationReason(contract: DecoratedEnterpriseContract | EnterpriseContract): string {
  return contract.endedAt ? contract.terminationReason ?? '' : '';
}

export function getFormattedBillingType(billingType: string) {
  const { ONE_TIME, QUARTERLY } = enterpriseContractBillingType;
  switch (billingType) {
    case ONE_TIME:
      return _t('Annual');
    case QUARTERLY:
      return _t('Quarterly');
    default:
      return '';
  }
}

export function isContractRefundable(contract: DecoratedEnterpriseContract) {
  const { refundDeadline, isRefunded } = contract;

  const isExpired = isContractExpired(contract);
  if (isRefunded || isExpired) {
    return false;
  }

  const now = new Date(Date.now()).getTime();
  return now < refundDeadline;
}

export function getPaymentMethodText(
  payment: {
    invoiceId?: string;
    paymentInformation?: {
      creditCardLastFourDigits?: string;
      creditCardType?: string;
      paymentEmail?: string;
    };
  },
  invoiceNumber?: string
) {
  const { paymentInformation, invoiceId } = payment;

  // Invoice number isn't backfilled on all invoices, this should render
  // `Invoice`
  // if not, it renders a paypal email due to a bug https://coursera.atlassian.net/browse/EMPLOYER-8031
  if (invoiceId || invoiceNumber) {
    return _t('Invoice #{invoiceNumber}', { invoiceNumber: invoiceNumber || '' });
  }

  if (!paymentInformation) {
    return undefined;
  }

  const { creditCardType, creditCardLastFourDigits, paymentEmail } = paymentInformation;
  if (creditCardType && creditCardLastFourDigits) {
    return `${creditCardType} **** ${creditCardLastFourDigits}`;
  } else if (paymentEmail) {
    const emailParts = paymentEmail?.split('@');
    const emailUsername = emailParts?.[0];
    const emailDomain = emailParts?.[1];

    return `Paypal ${emailUsername[0]}****${emailUsername[emailUsername.length - 1]}@${emailDomain}`;
  } else {
    return undefined;
  }
}

export const isUnpaidInvoiceContract = (contract: DecoratedEnterpriseContract) =>
  contract.contractType === EnterpriseContractType.SELF_SERVE &&
  contract.contractState === EnterpriseContractState.CREATED &&
  contract.billingSystemType === EnterpriseContractBillingSystemType.AUTOMATED_INVOICE;

export const getInvoiceUpsellDueDate = (
  contract: DecoratedEnterpriseContract
): { dueDate: number; isDueAfterRenewalInvoice: boolean } => {
  const now = new Date(Date.now()).valueOf();
  const now30days = now + 30 * 86400 * 1000;
  const endDate = contract.autoRenewInformation?.renewsAt || contract.endsAt;

  if (!endDate) {
    return { dueDate: now30days, isDueAfterRenewalInvoice: false };
  }

  const renewalInvoiceDate = endDate - 30 * 86400 * 1000;
  const isDueAfterRenewalInvoice = now30days > renewalInvoiceDate;

  // if the invoice has auto renew off, then return the due date (+30) directly
  const dueDate = isDueAfterRenewalInvoice ? renewalInvoiceDate : now30days;

  return { dueDate, isDueAfterRenewalInvoice };
};

export const getInvoiceDaysOverdue = (dueAt: number | undefined) => {
  const transactionDate = moment(dueAt).startOf('day');
  const now = moment();

  return now.diff(transactionDate, 'days');
};

export const isInvoiceOverdue = (transactionId: string | undefined, dueAt: number | undefined) => {
  // Ideally this will be determined by invoiceState,
  // when writing this, nothing switched the state on the BE
  if (!dueAt) {
    return false;
  }

  const now = new Date(Date.now());
  const transactionDate = new Date(dueAt);
  transactionDate.setHours(0, 0, 0, 0);
  return !transactionId && now > transactionDate;
};

export function calculateSelfServeContractProductPricesForContract(
  contract: DecoratedEnterpriseContract,
  numLicenses: number
) {
  const priceApi = API('/api/enterpriseSelfServeContractProductPrices.v1', { type: 'rest' });
  const uri = new URI()
    .addQueryParam('q', 'getEnterpriseSelfServeContractProductPriceForContract')
    .addQueryParam('userLimit', numLicenses)
    .addQueryParam('billingType', contract.billingType)
    .addQueryParam('termInstallmentsTotal', contract.billingType === 'ONE_TIME' ? 1 : 4)
    .addQueryParam('fields', 'actualLocalAmountDueToday')
    .addQueryParam('contractId', contract.id);

  if (contract.countryIsoCode) {
    uri.addQueryParam('countryIsoCode', contract.countryIsoCode);
  }

  if (contract.currencyCode) {
    uri.addQueryParam('currencyCode', contract.currencyCode);
  }

  return Promise.resolve(priceApi.get(uri.toString()));
}

export function createEnterpriseContractDeferredLicenseChange(contractId: string, numLicenses: number) {
  const licenseChangeApi = API('/api/enterpriseContractDeferredLicenseChanges.v1', { type: 'rest' });
  const uri = new URI();

  return Promise.resolve(
    licenseChangeApi.post(uri.toString(), {
      data: { enterpriseContractId: contractId, requestType: 'REQUEST', changeNumLicensesTo: numLicenses },
    })
  );
}
